import React, { useEffect, useState, ChangeEvent, Fragment, FormEvent, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LoginLabels } from "../../assets/labels/labels";
import { IconClient } from "../../assets/svgs-index";
import ButtonDivider from "../../components/buttons/ButtonDivider/ButtonDivider";
import PoweredBy from "../../components/PoweredBy/PoweredBy";
import { AuthenticationService } from "../../core/services/authentication.service";
import { Helper, HelperWebView } from "../../core/services/helper/helper.service";
import ModalInformation from "../../components/Modals/ModalInformation";
import LoginProvider from "./LoginProvider";
import PasswordField from "../../components/input/PasswordField";
import Cookies from "universal-cookie";
import LinkButton from "../../components/buttons/LinkButton/LinkButton";
import { CompanyService } from "../../core/services/company.service";
import Emitter from "../../core/services/emitter";
import AndroidBack from "../../core/services/androidBack.service";

declare global {
  interface OTPCredential extends Credential {
    code: string;
  }
  interface Window {
    receiveOTP: (otp: string) => void;
  }
}

interface ExtendedCredentialRequestOptions extends CredentialRequestOptions {
  otp?: {
    transport: string[];
  };
  signal?: AbortSignal;
}

const Login: React.FC<any> = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const [email, setEmail] = useState<string>(searchParams.get("email") || location?.state?.email || '');
  const [phone, setPhone] = useState<string>(searchParams.get("phone") || location?.state?.phone || '');
  const phoneRef = useRef(phone);
  const [inputUsernameValue, setInputUsernameValue] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [inputPhoneCode, setInputPhoneCode] = useState<string>("");
  const [phoneCode, setPhoneCode] = useState<string>("");
  const [phoneLoginLoading, setPhoneLoginLoading] = useState<boolean>(false);
  const [phoneCodeRequested, setPhoneCodeRequested] = useState<Boolean>(false);
  const [phoneCodeRequestSuccess, setPhoneCodeRequestSuccess] = useState<string>("");
  const [validateNow, setValidateNow] = useState<Boolean>(false);

  const cookies = new Cookies();
  const open_appointment = location?.state?.openAppointment || cookies.get("openAppointment");
  const isEmbedded = Helper.isEmbedded();
  const domain = location?.state?.domain || cookies.get("kssreturncompany")?.replace(".qa", "");

  const [modalInfoIsOpen, setModalInfoIsOpen] = useState<boolean>(false);
  const [modalInfoMessage, setModalInfoMessage] = useState<string>("");
  const [modalInfoSubMessage, setModalInfoSubMessage] = useState<string>("");
  const [modalInfoTitle, setModalInfoTitle] = useState<string>("");
  const [modalInfoConfirmText, setModalInfoConfirmText] = useState<string>("");
  const [modalInfoOnConfirm, setModalInfoOnConfirm] = useState<() => void>();

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneRegex = /(^(003519\d{8})$)|(^\+3519\d{8}$)|(^(?!00|0|\+)[9]\d{8}$)/;
  const phoneCodeRegex = /(^(?!\+|00)\d{6,}$)/;

  useEffect(() => {
    phoneRef.current = phone;
  }, [phone]);

  useEffect(() => {
    setInputUsernameValue(phone ? phone : email ? email : '');

    if (HelperWebView.isAndroidWebview()) {
      const handleLoginBack = () => {
        navigate('/');
      };
      Emitter.on('LOGIN_BACK', handleLoginBack);
      
      AndroidBack.setIsLogin(true);

      // Clean up function to remove event listener and reset setIsLogin
      return () => {
        // Remove event listener for 'LOGIN_BACK'
        Emitter.off('LOGIN_BACK', handleLoginBack);
        // Reset setIsLogin to false
        AndroidBack.setIsLogin(false);
      };
    }
    // eslint-disable-next-line 
  }, []);

  useEffect(() => {
    if (HelperWebView.isAndroidWebview()) {
      window.receiveOTP = (otp: string) => {
        setPhoneCodeDirectly(otp);
      };
      // Cleanup function to remove the function from window
      return () => {
        delete (window as any).receiveOTP;
      };
    }
    // eslint-disable-next-line 
  }, []); // Empty dependency array means this effect runs once on mount and cleanup on unmount



  let abortController = new AbortController();
  const receiveOtpBrowser = async () => {
    const options: ExtendedCredentialRequestOptions = {
      otp: { transport: ['sms'] },
      signal: abortController.signal,
    };

    if ('OTPCredential' in window) {
      try {
        const content = await navigator.credentials.get(options) as OTPCredential;
        setPhoneCodeDirectly(content.code);
      } catch (error) {
        console.error(error);
      }
    }
  };

  const tryStartOtpAndroid = () => {
    if (HelperWebView.isAndroidWebview()) {
      try {
        (window as any).Android.startOtpAndroid();
      } catch (error) {
        console.error(error);
      }
    }
  }


  const handleChangeUsername = (e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    setInputUsernameValue(input);
    if (emailRegex.test(input)) {
      setEmail(input);
      setPhone('');
    } else if (phoneRegex.test(input)) {
      setPhone(input);
      setEmail('');
    } else {
      setEmail('');
      setPhone('');
    }
    if (phoneCodeRequested) setPhoneCodeRequested(false);
  };

  const handleChangePassword = (value: string) => {
    setPassword(value);
  };

  const setPhoneCodeDirectly = (code: string) => {
    setInputPhoneCode(code);
    if (phoneCodeRegex.test(code)) {
      setPhoneCode(code);
      onPhoneLogin(phoneRef.current, code);
    } else {
      setPhoneCode('');
    }
  };

  const handleChangePhoneCode = (e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    setInputPhoneCode(input);
    if (phoneCodeRegex.test(input)) {
      setPhoneCode(input);
      onPhoneLogin(phone, input);
    } else {
      setPhoneCode('');
    }
  };

  const handlePhoneLogin: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    onPhoneLogin(phone, phoneCode);
  }

  const onPhoneLogin = (phone?: string, code?: string) => {
    setPhoneLoginLoading(true);
    let body: {
      user_id?: string; work_token?: string;
      domain?: string; work_email?: string; work_phone?: string; open_appointment?: boolean; is_cal_token?: string; phone?: string; code?: string;
    }
      = {
      phone,
      code
    };
    if (location.state?.userId || cookies.get("userId")) {
      body = {
        ...body,
        user_id: location.state?.userId || cookies.get("userId"),
        domain: domain,
        work_token: location.state?.liteToken || cookies.get("liteToken"),
        work_email: location.state?.liteEmail || cookies.get("liteEmail"),
        work_phone: location.state?.litePhone || cookies.get("litePhone"),
        is_cal_token: location.state?.isCalToken || cookies.get("isCalToken"),
      };
    } else {
      if (domain !== null) {
        body = {
          ...body,
          domain: domain,
        };
      }
    }
    if (open_appointment) {
      body = {
        ...body,
        open_appointment: open_appointment,
      }
    }
    AuthenticationService.authenticateUserPhone(body, navigate).then((response) => {
      if (response) {
        if (response.type === "Failure" || response.code) {
          setPhoneCodeRequestSuccess("Failure");
          const timer = setTimeout(() => {
            setPhoneCodeRequestSuccess("RequestAfterFailure");
          }, 3000);
          if (response.code && response.code !== '2') {
            setModalInfoTitle("Pedido Sem Sucesso");
            setModalInfoMessage("Foi encontrado um problema durante o pedido, por favor tente mais tarde.");
            setModalInfoConfirmText("OK");
            setModalInfoIsOpen(true);
          }
          // Cleanup function in case the component unmounts before the timer completes
          return () => clearTimeout(timer);
        }
      }
    });
    setTimeout(() => {
      setPhoneLoginLoading(false);
    }, 1000);
  };

  const handlePhoneCodeRequest: React.MouseEventHandler<HTMLElement> = (e) => {
    e.preventDefault();
    onPhoneCodeRequest(phone);
    setInputPhoneCode("");
    setPhoneCode("");
  }

  const onPhoneCodeRequest = (phone: string) => {
    const platform = HelperWebView.isAndroidWebview() ? "ANDROID" :
      HelperWebView.isIOSWebview() ? "IOS" :
        "WEB";
    const body: { phone: string, platform: string } = { phone, platform };
    AuthenticationService.requestPhoneCode(body, navigate).then((response) => {
      if (response) {
        if (response.type === "Success") {
          setPhoneCodeRequested(true);
          setPhoneCodeRequestSuccess("Success");
          receiveOtpBrowser(); //Listen to OTP on mobile browser
          tryStartOtpAndroid(); //Listen to OTP on Android app
          const timer = setTimeout(() => {
            setPhoneCodeRequestSuccess("");
          }, 10000);
          // Cleanup function in case the component unmounts before the timer completes
          return () => clearTimeout(timer);
        } else if(response.type === 409){
            iAgreeNotSet();
        } else {
          setPhoneCodeRequested(false);
          setModalInfoTitle("Tente novamente");
          setModalInfoMessage("Ocorreu um erro a enviar SMS com código de autenticação.");
          setModalInfoSubMessage("Tente novamente dentro de momentos.");
          setModalInfoConfirmText("OK");
          setModalInfoIsOpen(true);
        }
      }
    });
  };

  const handleEmailLogin = () => {
    onLogin(email, password);
  };

  const onLogin = (email?: string, password?: string, phone?: string, code?: string) => {
    let body: {
      email?: string; password?: string; user_id?: string; work_token?: string;
      domain?: string; work_email?: string; work_phone?: string; open_appointment?: boolean; is_cal_token?: string; phone?: string; code?: string;
    }
      = {
      email,
      password,
      phone,
      code
    };
    if (location.state?.userId || cookies.get("userId")) {
      body = {
        ...body,
        user_id: location.state?.userId || cookies.get("userId"),
        domain: domain,
        work_token: location.state?.liteToken || cookies.get("liteToken"),
        work_email: location.state?.liteEmail || cookies.get("liteEmail"),
        work_phone: location.state?.litePhone || cookies.get("litePhone"),
        is_cal_token: location.state?.isCalToken || cookies.get("isCalToken"),
      };
    } else {
      if (domain !== null) {
        body = {
          ...body,
          domain: domain,
        };
      }
    }
    if (open_appointment) {
      body = {
        ...body,
        open_appointment: open_appointment,
      }
    }
    AuthenticationService.authenticateUser(body, navigate).then((response) => {
      if (response) {
        setModalInfoTitle(response.title);
        setModalInfoMessage(response.message);
        setModalInfoSubMessage(response.subMessage);
        setModalInfoConfirmText(response.confirmText);
        response.onConfirm === "emailConfirm" && setModalInfoOnConfirm(() => handleConfirmEmail);
        setModalInfoIsOpen(true);
      }
    });
  };

  const handleConfirmEmail = () => {
    closeModal();

    let body: { email: string; password: string; user_id?: string; work_token?: string; domain?: string; work_email?: string; work_phone?: string; open_appointment?: boolean; is_cal_token?: string; } = {
      email,
      password,
    };
    if (location.state?.userId || cookies.get("userId")) {
      body = {
        ...body,
        user_id: location.state?.userId || cookies.get("userId"),
        domain: domain,
        work_token: location.state?.liteToken || cookies.get("liteToken"),
        work_email: location.state?.liteEmail || cookies.get("liteEmail"),
        work_phone: location.state?.litePhone || cookies.get("litePhone"),
        is_cal_token: location.state?.isCalToken || cookies.get("isCalToken"),
      };
    } else {
      if (domain !== null) {
        body = {
          ...body,
          domain: domain,
        };
      }
    }
    if (open_appointment) {
      body = {
        ...body,
        open_appointment: open_appointment,
      }
    }
    AuthenticationService.sendEmailConfirmation(body, navigate).then((response) => {
      if (response) {
        setModalInfoTitle(response.title);
        setModalInfoMessage(response.message);
        setModalInfoConfirmText(response.confirmText);
        setModalInfoIsOpen(true);
      }
    });
  };

  const iAgreeNotSet = () => {
    let url = "/registo";
    if (isEmbedded) {
      url += `?embedded=${encodeURIComponent(isEmbedded)}`;
    }
    navigate(url, { state: { email: email, phone: phone, errorIAgree: true } });
  };

  const iAgreeNotSetProvider = () => {
    let url = "/registo";
    if (isEmbedded) {
      url += `?embedded=${encodeURIComponent(isEmbedded)}`;
    }
    navigate(url, { state: { errorIAgree: true } });
  };

  const handleClickRegister = () => {
    let url = "/registo";
    if (isEmbedded) {
      url += `?embedded=${encodeURIComponent(isEmbedded)}`;
    }
    navigate(url, { state: { email: email, phone: phone } });
  };

  const handlePasswordFormSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();  // Prevent default form submission
    setValidateNow(true);
  }

  const renderModalInformation = (handler: any) => {
    if (!modalInfoIsOpen) {
      return <Fragment />;
    }
    return (
      <ModalInformation
        modalIsOpen={modalInfoIsOpen}
        title={modalInfoTitle}
        message={modalInfoMessage}
        subMessage={modalInfoSubMessage}
        confirmText={modalInfoConfirmText}
        onClose={() => closeModal()}
        onConfirm={handler || closeModal}
      />
    );
  };

  const closeModal = () => {
    setModalInfoIsOpen(false);
    setModalInfoTitle("");
    setModalInfoMessage("");
    setModalInfoSubMessage("");
    setModalInfoConfirmText("");
    setModalInfoOnConfirm(undefined);
  };

  const renderDeleteAccountLink = () => {
    return (
      <>{HelperWebView.isIOSWebview() && <div className="w-100 d-flex align-center">
        <LinkButton
          onClick={() => {
            navigate("/help-delete-account");
          }}
          styleClass="link-container mt-5"
          textClass="small"
          valueText="E se mais tarde quiser apagar a minha conta?"
        />
      </div>
      }</>
    );
  }

  const renderGoToRegisterLink = () => {
    return (
      <div className="link-container mt-4 text-center" onClick={handleClickRegister}
          ref={inputRef}>
        {LoginLabels.linkMessage}
      </div>
    );
  }

  const renderUsernameInput = () => {
    // Define the onKeyDown event handler
    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
  //   event.preventDefault();
      if (event.key === 'Enter' && !phoneCodeRequested && phone) { onPhoneCodeRequest(phone); }
      if (event.key === 'Enter' && email) {
        // Find all focusable elements, ensure each is an HTMLElement.
        const focusableElementsSelector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
        const focusableElements = Array.from(document.querySelectorAll(focusableElementsSelector))
          .filter((elem): elem is HTMLElement => elem instanceof HTMLElement && !elem.hasAttribute('disabled'));

        const currentIndex = focusableElements.indexOf(event.target as HTMLElement);
        const nextIndex = (currentIndex + 1) % focusableElements.length;
        const nextElement = focusableElements[nextIndex];

        nextElement.focus();
      }
     
   };
    return (
      <div className="input-container">
        <p className="input-label">E-mail ou Nº de Telemóvel</p>
        <input
          className="input-medium-default "
          type="text"
          title="E-mail ou Nº de Telemóvel de Portugal"
          value={inputUsernameValue}
          placeholder="E-mail ou Nº de Telemóvel de Portugal"
          onChange={handleChangeUsername}
          onKeyDown={handleKeyDown}
          autoComplete="on"
        />
      </div>
    );
  }

  const inputRef = useRef<HTMLInputElement>(null);

  const handleFocus = () => {
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      }
    }, 300); // Adjust the delay time as needed
  };

  const renderSMSCodeInput = () => {
    return (<>
      {phone && phoneCodeRequested && <>
        <div className="input-container mt-4">
          {/*<p className="input-label">Introduza o código recebido por SMS</p>*/}
          <input
            className={`input-medium-default unique-letter-spacing bigPlaceholder
            ${phoneCodeRequestSuccess === "Success" ? "success" :
                phoneCodeRequestSuccess === "Failure" ? "failure" : ''
              }`}
            type="text"
            name="one-time-code"
            id="sms-code"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="one-time-code"
            maxLength={6}
            aria-label="One time password"
            //autoFocus
            title="Código rebido por SMS"
            value={inputPhoneCode}
            placeholder="Introduza o código recebido por SMS"
            onChange={handleChangePhoneCode}
            onFocus={handleFocus}
          />
        </div>
        <div
          className={`link-container mt-1 ${phoneCodeRequestSuccess === "Success" ? "success" :
            phoneCodeRequestSuccess === "Failure" ? "failure" : ''
            }`}
          onClick={phoneCodeRequestSuccess === 'RequestAfterFailure' ? handlePhoneCodeRequest : undefined}
        >
          {
            phoneCodeRequestSuccess === "Success" ? "" : //"Enviámos-lhe um Código por SMS." :
            phoneCodeRequestSuccess === "Failure" ? "Código inválido ou expirado." :
              phoneCodeRequestSuccess === "RequestAfterFailure" ? "Enviem-me novo código SMS!" : ""
          }
        </div>
      </>
      }
    </>);
  }

  //Botão fictício para aparecer sempre que não estejam disponíveis outros botões
  const renderDummyContinuarButton = () => {
    return (<>
      {!phoneCodeRequested && !phone && !email &&
        <button className="cta-button-primary disabled d-medium w-100 mt-4">
          Continuar
        </button>
      }
    </>);
  }

  const renderRequestPhoneOTPButton = () => {
    return (<>
      {!phoneCodeRequested && phone &&
        <button className="cta-button-primary d-medium w-100 mt-4" onClick={handlePhoneCodeRequest} type="button">
          Continuar
        </button>}
    </>)
  }

  const renderLoginPhoneButton = () => {
    return (<>
      {phone && phoneCodeRequested &&
        <button className={`cta-button-primary d-medium w-100 mt-4 ${(!phoneCode || phoneLoginLoading) ? "disabled" : "" }`}
          onClick={handlePhoneLogin}
          type="button"
          disabled={!phoneCode || phoneLoginLoading}
          >
          Entrar
        </button>
      }
    </>)
  }

  const handleValidationComplete = (isValid: boolean) => {
    if (isValid) { handleEmailLogin(); }
    setValidateNow(false);
  }

  const handlePasswordRecovery = () => {
    let url = "/recuperar-password";
    if (isEmbedded) {
      url += `?embedded=${encodeURIComponent(isEmbedded)}`;
    }
    navigate(url, { state: {email} });
  }

  const renderEmailPasswordFieldAndButton = () => {
    return (<>
      {email &&
        <form onSubmit={handlePasswordFormSubmit}>
          <PasswordField password={password}
            onChange={handleChangePassword}
            validateNow={validateNow}
            onValidationComplete={handleValidationComplete}
            autoComplete="current-password" />
          <div
            className="link-container mt-1"
            onClick={() => handlePasswordRecovery()}>
            {LoginLabels.passwordRecovery}
          </div>
          <button className="cta-button-primary d-medium w-100 mt-4" type="submit">Entrar</button>
        </form>
      }</>)
  }

  const returnToLite = () => {
    const returnUrl = cookies.get("redirectAuthFallback");
    if (returnUrl) {
      window.open(returnUrl, '_self');
    }
  }

  const renderEmbeddedHeader = () => {
    return (
      <>
        <div className="header w-100 d-flex flex-row justify-content-between ps-md-9 opacity-50">
          <div className="logo-container d-flex flex-column">
            <div className="company-logo mt-2 mx-3 mx-md-4">
              <img className="kmclient-logo"
              src={domain ? CompanyService.urlCompanyImage(`domain/${domain}`) : IconClient}
              alt="logo"
              onError={(e) => ((e.target as HTMLImageElement).src = IconClient)} />
            </div>
          </div>
          <div className="app-logo-container me-3 me-md-4 align-items-center">
            {" "}
            <PoweredBy isTop={true}/>
          </div>
        </div>
      </>
    )
  }

  const containerStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.7)', // semi-transparent background
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000, // Ensure it's on top of other elements
    maxWidth: 'none',

  };

  const iframeContainerStyle = {
    width: '96%',
    height: '200%',
    backgroundColor: '#fff',
    borderRadius: '8px',
    //overflow: 'hidden',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    position: 'relative',
  };

  const closeButtonStyle: React.CSSProperties = {
    top: '10px',
    right: '10px',
    width: '36px',
    height: '36px',
    color: 'black',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    fontSize: '24px',
    fontWeight: 'bold',
    lineHeight: '24px',
    position: 'absolute',
  };

  const embeddedContainerStyle = isEmbedded ? containerStyle : {};
  const embeddedIframeContainerStyle = isEmbedded ? iframeContainerStyle : {};

  const renderPrivacyTerms = () => {
    const showPrivacyTermsFooter = true; //HelperWebView.isAndroidWebview();
    return (
      <>
        <footer className="footer h-100 d-flex flex-column pt-3">
          <div className="d-flex flex-fill" />  {/* Spacer to push footer content down */}
          {showPrivacyTermsFooter && (
            <div className="d-inline-flex justify-content-center align-items-center">
              <LinkButton
                onClick={() => Emitter.emit("OPEN_IAGREE_TEXT", null)}
                styleClass="link-container ms-0 ms-md-2"
                textClass="small"
                valueText="Política de Privacidade"
              />
              <span className="mx-2 small">|</span>
              <LinkButton
                onClick={() => window.open('https://www.keymaster.pt/termos-e-condicoes/', '_self')}
                styleClass="link-container ms-0 ms-md-2"
                textClass="small"
                valueText="Termos & Condições"
              />
            </div>
          )}
        </footer>
      </>
    )
  }

  return (
    <div className={`main-page-container pb-1 pt-4 align-center justify-content-start ${isEmbedded ? 'mt-0 ' : 'mt-0 container'}`} style={embeddedContainerStyle}>
      {!isEmbedded &&
        <>
          <div className="company-logo-container mt-2 mt-md-4" onClick={() => !isEmbedded? navigate("/") : {}}>
            <img className="kmclient-logo"
              src={domain ? CompanyService.urlCompanyImage(`domain/${domain}`) : IconClient}
              alt="logo"
              onError={(e) => ((e.target as HTMLImageElement).src = IconClient)} />
          </div>
        </>
      }  
      {isEmbedded && renderEmbeddedHeader()}
      {isEmbedded ? (
        <div style={embeddedIframeContainerStyle}>
          <div onClick={returnToLite} style={closeButtonStyle} > &times; </div>
          <h3 className="text-center w-100 my-4">{open_appointment ? LoginLabels.header_appointment : LoginLabels.header}</h3>
          <div className="grey-box-container py-4 px-6 w-100">
            <div className="box-form-container">
              {renderUsernameInput()}
              {renderSMSCodeInput()}
              {renderDummyContinuarButton()}
              {renderEmailPasswordFieldAndButton()}
              {renderRequestPhoneOTPButton()}
              {renderLoginPhoneButton()}
            </div>
          </div>
          <ButtonDivider />
          <LoginProvider provider="google" navigate={navigate} iAgreeNotSet={iAgreeNotSetProvider} login={true} />
          <LoginProvider provider="apple" navigate={navigate} iAgreeNotSet={iAgreeNotSetProvider} login={true} />
          {renderGoToRegisterLink()}
          {!isEmbedded &&
            <>
              {renderDeleteAccountLink()}

              <div className="powered-by-container mt-3">
                {" "}
                <PoweredBy />
              </div>
            </>
          }
          {renderModalInformation(modalInfoOnConfirm)}
        </div>)
      :
      (<><h3 className="text-center w-100 my-4">{open_appointment ? LoginLabels.header_appointment : LoginLabels.header}</h3>
        <div className="grey-box-container py-4 px-6 w-100">
          <div className="box-form-container">
            {renderUsernameInput()}
            {renderSMSCodeInput()}
            {renderDummyContinuarButton()}
            {renderEmailPasswordFieldAndButton()}
            {renderRequestPhoneOTPButton()}
            {renderLoginPhoneButton()}
          </div>
        </div>
        <ButtonDivider />
        <LoginProvider provider="google" navigate={navigate} iAgreeNotSet={iAgreeNotSetProvider} login={true}/>
        <LoginProvider provider="apple" navigate={navigate} iAgreeNotSet={iAgreeNotSetProvider} login={true}/>
        {renderGoToRegisterLink()}
        {!isEmbedded &&
          <>
            {renderDeleteAccountLink()}

            <div className="powered-by-container mt-3">
              {" "}
              <PoweredBy />
            </div>
          </>
        }
        {renderPrivacyTerms()}
        {renderModalInformation(modalInfoOnConfirm)}
        </>)
      }
    </div>
  );
};

export default Login;
